import Phaser from "phaser";
import { GAME_VARIABLES, GameEvents, SceneName, DEV_MODE } from "../constants";
import { getDevEmitter, dev_event } from "../../devUI/devEvents";
import { getDevApplicationState } from "../../devUI/devApplicationGlobal";

const UNDO_KEY = "KeyZ";

/**
 * TODO HACK: in the future spend the frame figuring out what
 * the user intended to press and then submit it to the
 * next frame. Then there is no chance of two keys
 * being detected on the same frame.
 *
 * Buffer input.
 */
export default class TurnBasedInputPlugin extends Phaser.Plugins.BasePlugin {
  // private keys: Set<string>;
  private justPressed: string | null;
  private lastPressed: string | null;
  private debounced: boolean;
  private forceBlock: boolean;
  private isRecording: boolean;
  private recordedKeys: String[];
  private isPlaying: boolean;
  private stopPlayback: boolean;
  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);

    // this.keys = new Set();
    this.justPressed = null;
    this.lastPressed = null;
    this.debounced = false;
    this.forceBlock = false;
    this.isRecording = false;
    this.recordedKeys = [];
    this.isPlaying = false;
    this.stopPlayback = false;

    getDevEmitter()?.on(
      dev_event.START_RECORDING,
      () => {
        this.isRecording = true;
      },
      this
    );
    getDevEmitter()?.on(dev_event.STOP_RECORDING, async () => {
      if (this.recordedKeys.length > 0) {
        getDevEmitter()?.emit(dev_event.SAVE_RECORDING, this.recordedKeys);
        if (DEV_MODE) {
          await fetch(
            `/api/post-recording/${getDevApplicationState()?.currentLevelKey}`,
            {
              method: `POST`,
              mode: `cors`,
              cache: `no-cache`,
              credentials: `same-origin`,
              referrerPolicy: "no-referrer",
              body: JSON.stringify(this.recordedKeys),
              headers: { "Content-Type": "application/json" },
            }
          );
        }
      }
      this.stopRecording();
    });
    getDevEmitter()?.on(dev_event.SKIP_TO_LEVEL, () => {
      this.stopRecording();
    });
    getDevEmitter()?.on(dev_event.STOP_PLAYBACK, () => {
      this.stopPlayback = true;
    });

    getDevEmitter()?.on(
      dev_event.PLAY_Recording,
      (keypresses) => {
        if (this.isPlaying) {
          return;
        }
        this.playRecording(keypresses);
      },
      this
    );
  }

  get isJustPressedLeft() {
    return this.justPressed === "KeyA" || this.justPressed === "ArrowLeft";
  }
  get isJustPressedRight() {
    return this.justPressed === "KeyD" || this.justPressed === "ArrowRight";
  }

  get isJustPressedDown() {
    return this.justPressed === "KeyS" || this.justPressed === "ArrowDown";
  }
  get isJustPressedUp() {
    return this.justPressed === "KeyW" || this.justPressed === "ArrowUp";
  }
  get isJustPressedAction() {
    return this.justPressed === "Space";
  }
  get isUndoPressed() {
    return this.justPressed === UNDO_KEY;
  }

  get isRestartPressed() {
    return this.justPressed === "KeyR";
  }

  get isEscapePressed() {
    return this.justPressed === "Escape" || this.justPressed === "Backspace";
  }

  start() {
    const eventEmitter = this.game.events;
    window.addEventListener("keydown", this.handleKeyDown);

    eventEmitter.on("postrender", this.postrender, this);
  }

  recordKey() {
    // Implementation in constructor
    if (this.isRecording && this.lastPressed) {
      if (this.lastPressed === UNDO_KEY) {
        this.recordedKeys.pop();
      } else {
        this.recordedKeys.push(this.lastPressed);
      }
    }
  }

  stopRecording() {
    this.isRecording = false;
    this.recordedKeys = [];
  }

  async playRecording(keys: string[]) {
    this.isPlaying = true;
    this.isRecording = false;
    for (const key of keys) {
      if (this.stopPlayback) {
        break;
      }
      this.justPressed = key;
      await new Promise((resolve, _) =>
        setTimeout(() => {
          resolve();
        }, GAME_VARIABLES.MOVE_TIME_MS + 25)
      );
    }
    this.stopPlayback = false;
    this.isPlaying = false;
  }

  handleKeyDown = ({ code }: { code: string }) => {
    if (this.isPlaying) {
      return;
    }
    if (this.forceBlock) {
      return;
    }
    if (this.debounced) {
      return;
    } else if (code === UNDO_KEY) {
      this.justPressed = UNDO_KEY;
    }
    this.justPressed = code;
    this.lastPressed = this.justPressed;
  };

  triggerForceDebounce() {
    this.forceBlock = true;
    this.justPressed = null;
  }

  removeForceDebounce() {
    this.forceBlock = false;
  }

  postrender() {
    if (this.justPressed) {
      this.debounced = true;
      this.game.scene
        .getScene(SceneName.StartScene)
        ?.events.emit(GameEvents.TurnDebounceStart);
      setTimeout(
        () => {
          if (typeof this?.debounced === "boolean") {
            this.debounced = false;
          }
        },
        this.isUndoPressed ? 25 : GAME_VARIABLES.MOVE_TIME_MS
      );
    }
    this.justPressed = null;
  }

  destroy() {
    const eventEmitter = this.game.events;
    eventEmitter.removeListener("postrender", this.postrender, this);
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  static registerConfig() {
    return {
      key: "TurnBasedInputPlugin",
      plugin: TurnBasedInputPlugin,
      start: false,
      mapping: "myInput",
    };
  }
}
